/**
 * DESTASH PAGE
 * 
 * Public view of a user's destash listings
 * Similar to PublicWishlist page but for items marked for sale/swap
 * 
 * Features:
 * - View user's available destash items
 * - Filter by item type
 * - Sort by price, date added
 * - Contact seller via messaging
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';
import { 
  ArrowLeft, 
  Loader2, 
  ShoppingBag, 
  MessageCircle, 
  Filter,
  X,
  Repeat,
  ExternalLink
} from 'lucide-react';

interface DestashItem {
  id: string;
  itemType: string;
  name: string;
  brand: string;
  photos?: { url: string; isDefault?: boolean }[];
  destashPrice?: number;
  destashCondition?: string;
  isOpenToSwaps?: boolean;
  destashNotes?: string;
  destashDate?: string;
}

interface UserProfile {
  username: string;
  displayName?: string;
  avatarUrl?: string;
  privacy?: {
    profileVisibility?: string;
    allowDestashContact?: boolean;
  };
}

export default function DestashPage() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<DestashItem[]>([]);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<DestashItem | null>(null);
  
  // Get item ID from URL query param (for deep linking from notifications)
  const highlightItemId = searchParams.get('item');
  
  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [itemTypeFilter, setItemTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'price-low' | 'price-high' | 'recent'>('recent');

  // Check if viewing own destash
  const currentUser = getCurrentUser();
  const isOwnDestash = currentUser?.uid === ownerId;

  useEffect(() => {
    const fetchDestash = async () => {
      if (!username) {
        setError('No username provided');
        setLoading(false);
        return;
      }

      try {
        // Find user by username
        const usersQuery = query(
          collection(db, 'users'),
          where('profile.username', '==', username)
        );
        const usersSnapshot = await getDocs(usersQuery);

        if (usersSnapshot.empty) {
          // Try without profile prefix
          const usersQuery2 = query(
            collection(db, 'users'),
            where('username', '==', username)
          );
          const usersSnapshot2 = await getDocs(usersQuery2);
          
          if (usersSnapshot2.empty) {
            setError('User not found');
            setLoading(false);
            return;
          }
          
          const userDoc = usersSnapshot2.docs[0];
          setOwnerId(userDoc.id);
          const userData = userDoc.data();
          setProfile({
            username: userData.username || username,
            displayName: userData.displayName || userData.profile?.displayName,
            avatarUrl: userData.avatarUrl || userData.profile?.avatarUrl,
            privacy: userData.privacy || userData.profile?.privacy
          });
        } else {
          const userDoc = usersSnapshot.docs[0];
          setOwnerId(userDoc.id);
          const userData = userDoc.data();
          setProfile({
            username: userData.profile?.username || username,
            displayName: userData.profile?.displayName || userData.displayName,
            avatarUrl: userData.profile?.avatarUrl || userData.avatarUrl,
            privacy: userData.profile?.privacy || userData.privacy
          });
        }

        // Fetch destash items
        const userId = usersSnapshot.empty 
          ? (await getDocs(query(collection(db, 'users'), where('username', '==', username)))).docs[0]?.id
          : usersSnapshot.docs[0].id;

        if (userId) {
          const itemsQuery = query(
            collection(db, 'users', userId, 'items'),
            where('isForDestash', '==', true),
            where('destashStatus', '==', 'available')
          );
          const itemsSnapshot = await getDocs(itemsQuery);
          
          const destashItems: DestashItem[] = itemsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as DestashItem));
          
          setItems(destashItems);
        }
      } catch (err) {
        console.error('Error fetching destash:', err);
        setError('Failed to load destash');
      } finally {
        setLoading(false);
      }
    };

    fetchDestash();
  }, [username]);

  // Auto-open modal if item ID is in URL (from notification deep link)
  useEffect(() => {
    if (highlightItemId && items.length > 0 && !selectedItem) {
      const itemToOpen = items.find(item => item.id === highlightItemId);
      if (itemToOpen) {
        setSelectedItem(itemToOpen);
        // Clear the query param so refreshing doesn't re-open
        setSearchParams({}, { replace: true });
      }
    }
  }, [highlightItemId, items, selectedItem, setSearchParams]);

  // Filter and sort items
  const filteredItems = items
    .filter(item => itemTypeFilter === 'all' || item.itemType === itemTypeFilter)
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return (a.destashPrice || 0) - (b.destashPrice || 0);
        case 'price-high':
          return (b.destashPrice || 0) - (a.destashPrice || 0);
        case 'recent':
        default:
          return new Date(b.destashDate || 0).getTime() - new Date(a.destashDate || 0).getTime();
      }
    });

  // Get item type label
  const getItemTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      polish: '💅 Polish',
      plate: '🔲 Plate',
      tool: '🛠️ Tool',
      acrylic: '💪 Acrylic',
      embellishment: '💎 Embellishment',
      powder: '✨ Powder',
      decal: '🦋 Decal/Transfer'
    };
    return types[type] || type;
  };

  // Get item types for filter
  const itemTypes = [...new Set(items.map(i => i.itemType))];

  // Get item image
  const getItemImage = (item: DestashItem) => {
    if (!item.photos?.length) return null;
    const defaultPhoto = item.photos.find(p => p.isDefault);
    return defaultPhoto?.url || item.photos[0]?.url;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-glass" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-glass mb-2">{error}</h2>
          <button
            onClick={() => navigate(-1)}
            className="text-glass hover:text-glass-secondary"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-800/60 rounded-lg transition-default"
          >
            <ArrowLeft className="icon-base text-glass-secondary" />
          </button>
          
          <div className="flex items-center gap-4 flex-1">
            {profile?.avatarUrl ? (
              <img 
                src={profile.avatarUrl} 
                alt={profile.displayName || profile.username}
                className="w-14 h-14 rounded-full object-cover border-2 border-slate-600"
              />
            ) : (
              <div className="w-14 h-14 rounded-full rainbow-gradient flex items-center justify-center text-white text-xl font-bold">
                {(profile?.displayName || profile?.username || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-glass font-heading">
                {profile?.displayName || profile?.username}'s Destash
              </h1>
              <p className="text-glass-secondary font-body">
                {items.length} item{items.length !== 1 ? 's' : ''} available
              </p>
            </div>
          </div>

          {/* View Profile Link */}
          <Link
            to={`/profile/${username}`}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800/60 hover:bg-white/70 rounded-lg text-glass-secondary transition-default font-body"
          >
            <ExternalLink className="icon-sm" />
            View Profile
          </Link>
        </div>

        {items.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <ShoppingBag className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-glass mb-2 font-body">
              No items for destash
            </h2>
            <p className="text-glass-secondary font-body">
              {isOwnDestash 
                ? "You haven't listed any items for destash yet."
                : "This user doesn't have any items listed for destash."}
            </p>
            {isOwnDestash && (
              <Link
                to="/collection"
                className="inline-block mt-4 px-6 py-2 btn-primary rounded-lg transition-default"
              >
                Go to Collection
              </Link>
            )}
          </div>
        ) : (
          <>
            {/* Filters */}
            <div className="glass-card p-4 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800/60 hover:bg-white/80 rounded-lg text-glass-secondary transition-default"
                >
                  <Filter className="icon-sm" />
                  Filters
                </button>
                
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                  className="px-4 py-2 bg-slate-800/60 rounded-lg border-0 text-glass-secondary font-body"
                >
                  <option value="recent">Most Recent</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>

                {itemTypeFilter !== 'all' && (
                  <button
                    onClick={() => setItemTypeFilter('all')}
                    className="flex items-center gap-1 px-3 py-1 bg-slate-700 text-glass-secondary rounded-full text-sm"
                  >
                    {getItemTypeLabel(itemTypeFilter)}
                    <X className="icon-xs" />
                  </button>
                )}
              </div>

              {showFilters && (
                <div className="mt-4 pt-4 border-t border-slate-600">
                  <p className="text-sm font-medium text-glass-secondary mb-2">Item Type</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setItemTypeFilter('all')}
                      className={`px-3 py-1.5 rounded-full text-sm transition-default ${
                        itemTypeFilter === 'all'
                          ? 'bg-slate-700 text-white'
                          : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                      }`}
                    >
                      All ({items.length})
                    </button>
                    {itemTypes.map(type => (
                      <button
                        key={type}
                        onClick={() => setItemTypeFilter(type)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-default ${
                          itemTypeFilter === type
                            ? 'bg-slate-700 text-white'
                            : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                        }`}
                      >
                        {getItemTypeLabel(type)} ({items.filter(i => i.itemType === type).length})
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Items Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredItems.map((item) => (
                <div 
                  key={item.id} 
                  className="bg-white rounded-xl shadow-md overflow-hidden group cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => setSelectedItem(item)}
                >
                  {/* Image */}
                  <div className="aspect-square relative overflow-hidden">
                    {getItemImage(item) ? (
                      <img
                        src={getItemImage(item)!}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center text-4xl">
                        🏷️
                      </div>
                    )}
                    
                    {/* Price badge */}
                    <span className="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-bold bg-green-500 text-white">
                      ${item.destashPrice?.toFixed(2) || '0.00'}
                    </span>

                    {/* Swaps badge */}
                    {item.isOpenToSwaps && (
                      <span className="absolute top-2 right-2 p-1.5 rounded-full bg-blue-500 text-white" title="Open to swaps">
                        <Repeat className="icon-xs" />
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <p className="text-xs text-glass font-bold truncate font-heading">
                      {item.brand}
                    </p>
                    <p className="font-bold text-glass truncate font-body">
                      {item.name}
                    </p>
                    {item.destashCondition && (
                      <p className="text-xs text-glass-muted mt-1">
                        {item.destashCondition}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Item Detail Modal */}
        {selectedItem && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedItem(null)}
          >
            <div 
              className="glass-modal max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Image */}
              <div className="aspect-square relative">
                {getItemImage(selectedItem) ? (
                  <img
                    src={getItemImage(selectedItem)!}
                    alt={selectedItem.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center text-6xl">
                    🏷️
                  </div>
                )}
                <button
                  onClick={() => setSelectedItem(null)}
                  className="absolute top-4 right-4 p-2 bg-white/80 hover:bg-white rounded-full"
                >
                  <X className="icon-md" />
                </button>
              </div>

              {/* Details */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm text-glass font-bold font-heading">
                      {selectedItem.brand}
                    </p>
                    <h2 className="text-xl font-bold text-glass font-body">
                      {selectedItem.name}
                    </h2>
                    <p className="text-sm text-glass-muted">{getItemTypeLabel(selectedItem.itemType)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">
                      ${selectedItem.destashPrice?.toFixed(2) || '0.00'}
                    </p>
                    {selectedItem.isOpenToSwaps && (
                      <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                        <Repeat className="icon-xs" /> Open to swaps
                      </span>
                    )}
                  </div>
                </div>

                {selectedItem.destashCondition && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-glass-secondary">Condition</p>
                    <p className="text-glass">{selectedItem.destashCondition}</p>
                  </div>
                )}

                {selectedItem.destashNotes && (
                  <div className="mb-6">
                    <p className="text-sm font-medium text-glass-secondary">Notes</p>
                    <p className="text-glass">{selectedItem.destashNotes}</p>
                  </div>
                )}

                {/* Contact Button */}
                {!isOwnDestash && currentUser && (
                  <button
                    onClick={() => {
                      // TODO: Open message modal or navigate to messages
                      navigate(`/messages?to=${ownerId}&item=${selectedItem.id}`);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 btn-primary rounded-xl font-bold transition-default font-body"
                  >
                    <MessageCircle className="icon-md" />
                    Contact Seller
                  </button>
                )}

                {!currentUser && (
                  <p className="text-center text-glass-muted text-sm">
                    Sign in to contact the seller
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
